import React, { useCallback } from 'react';
import { MdOpenInNew } from 'react-icons/md';

import { MOUSE_BUTTON } from '../common/constants';
import { useNavigate } from 'react-router';
import { IconButton, IconButton_IconSizes } from '../IconButton/IconButton';

interface OpenLinkButtonProps {
  /* Link to open */
  link: string;
  /* Tooltip to show on hover */
  tooltip: React.ReactNode;
  /* Optional callback to call on the normal left click */
  onButtonClick?: () => void;
  /* If the link is external, a browser mechanism is used to open the link; otherwise React router is used to open the link */
  isExternal?: boolean;
}

const icon = <MdOpenInNew size={IconButton_IconSizes.xSmall} />;

/**
 * Prevents unwanted behavior of bubbling click to an underlying component
 * @param e
 */
const handleClick: React.MouseEventHandler<HTMLButtonElement> = e => {
  e.preventDefault();
};

/**
 * Button that opens a link in a new tab on middle click or Ctrl+LeftClick,
 * or calls onButtonClick on normal left click if provided.
 * Uses standard link icon of xSmall size.
 * @param link - link to open
 * @param tooltip - tooltip to show on hover
 * @param onButtonClick - optional callback to call on normal left click
 * @param isExternal - if true, opens the link using a browser mechanism, otherwise uses React router (default)
 */
export const OpenLinkButton: React.FC<OpenLinkButtonProps> = ({
  link,
  tooltip,
  onButtonClick,
  isExternal = false,
}) => {
  const navigate = useNavigate();
  /**
   * `onMouse` events accept more button types, `onClick` accepts only left mouse
   * - we want to open the link on the middle button click
   */
  const handleMouseDown = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      const isMiddleClick = e.button === MOUSE_BUTTON.middle;
      const isCtrlModifiedLeft = e.button === MOUSE_BUTTON.left && e.ctrlKey;
      const isPlainLeft = e.button === MOUSE_BUTTON.left && !e.ctrlKey;

      if (!(isMiddleClick || isCtrlModifiedLeft || isPlainLeft)) {
        return;
      }

      e.preventDefault();

      const openInNewTab = () => {
        const newTab = window.open(link, '_blank', 'noopener,noreferrer');
        if (newTab) {
          newTab.opener = null;
        }
      };

      const openInSameTab = () => {
        window.location.href = link;
      };

      if (isMiddleClick || isCtrlModifiedLeft) {
        openInNewTab();
        return;
      } else if (isPlainLeft) {
        if (onButtonClick) {
          onButtonClick();
          return;
        } else {
          if (isExternal) {
            openInSameTab();
          } else {
            navigate(link);
          }
        }
      }
    },
    [link, onButtonClick, isExternal, navigate],
  );

  return (
    <IconButton
      icon={icon}
      size="xSmall"
      tooltip={tooltip}
      onClick={handleClick} //cancels underlying component click
      onMouseDown={handleMouseDown} //opens given url or calls onButtonClick if provided
    />
  );
};
